import { Box } from '@withjoy/joykit';
import React, { forwardRef } from 'react';
import { VimeoVideo, useOptimizedVimeoReference } from '@shared/components/VimeoVideo';
import type { CurationHeroHeaderCurationHeader } from '../../CurationHeroHeader.types';
import { StyledBackgroundImage, StyledBackgroundVideo, StyledBackgroundVideoContainer, StyledGradient, styles } from './HeroBackground.styles';

type HeroBackgroundArgs = {
  curationHeader: CurationHeroHeaderCurationHeader;
  isSliderItem?: boolean;
  disableBackgroundGradient?: boolean;
  overrideBgImageUrl?: string;
  /** Hold the optimized player, e.g. while the curation's video dialog covers it. Flag off: ignored. */
  paused?: boolean;
};

/**
 * The curation's background video or image. `ref` reaches the legacy iframe only (flag off), which
 * callers use to restart it after their video dialog closes; the optimized player is held with
 * `paused` instead and never attaches the ref.
 */
const HeroBackground = forwardRef<HTMLIFrameElement, HeroBackgroundArgs>((props, ref) => {
  const { curationHeader, isSliderItem, disableBackgroundGradient = false, overrideBgImageUrl, paused } = props;
  const { eyebrow, title, backgroundImage, video, videoThumbnail } = curationHeader;
  const bgImageUrl = overrideBgImageUrl ? overrideBgImageUrl : backgroundImage?.url;
  const { video: optimizedVideo, isFlagLoading: isVideoFlagLoading } = useOptimizedVimeoReference(video);
  return (
    <Box __css={styles.mediaContainer}>
      <StyledGradient __css={styles.gradient(!isSliderItem, disableBackgroundGradient)} />
      {video && (
        <StyledBackgroundVideo __css={styles.backgroundVideo(videoThumbnail?.url)}>
          <StyledBackgroundVideoContainer __css={styles.backgroundVideoContainer}>
            {isVideoFlagLoading ? null : optimizedVideo ? (
              <Box __css={styles.videoCover}>
                <VimeoVideo
                  videoId={optimizedVideo.videoId}
                  privacyHash={optimizedVideo.privacyHash}
                  title={`${eyebrow}-${title}`}
                  paused={paused}
                />
              </Box>
            ) : (
              <iframe
                ref={ref}
                src={`${video}&background=1`}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={`${eyebrow}-${title}`}
              />
            )}
          </StyledBackgroundVideoContainer>
        </StyledBackgroundVideo>
      )}
      {!video && bgImageUrl && <StyledBackgroundImage __css={styles.backgroundImage(bgImageUrl)} />}
    </Box>
  );
});

export default HeroBackground;
